import moment from 'moment';

export const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * An earlier day of the live grid: the cells are history, not something to
 * work in. Snapshots are a different view and stay out of this.
 * @param localDate the day being viewed
 * @param today today's calendar day
 * @param isSnapshot set when viewing a submitted snapshot
 */
export const isTalentReviewHistoryDay = (localDate: string, today: string, isSnapshot?: boolean) =>
	!isSnapshot && localDate < today;

/**
 * Cycle end dates are calendar days stored as UTC midnight. Count remaining
 * days against UTC today so the number matches `formatTalentReviewDeadline`.
 * @param endDate the cycle end date
 */
export const talentReviewDaysLeft = (endDate: string | Date) =>
	moment.utc(endDate).startOf('day').diff(moment.utc().startOf('day'), 'days');

export const formatTalentReviewDeadline = (endDate: string | Date) =>
	moment.utc(endDate).format('LL');

/**
 * Label for a day in the talent review calendar. Today / yesterday / the day
 * before yesterday stay relative (those strings come from i18n). Older days
 * use moment `LL`, which follows the workspace locale.
 * @param day the calendar day to label
 * @param today today's calendar day
 * @param labels relative day names
 * @param labels.today label for today
 * @param labels.yesterday label for yesterday
 * @param labels.dayBeforeYesterday label for the day before yesterday
 * @param locale workspace locale for older days
 */
export const formatTalentReviewDay = (
	day: string,
	today: string,
	labels: { today: string; yesterday: string; dayBeforeYesterday: string },
	locale?: string,
) => {
	const daysAgo = moment(today, DATE_FORMAT).diff(moment(day, DATE_FORMAT), 'days');

	if (daysAgo === 0) {
		return labels.today;
	}

	if (daysAgo === 1) {
		return labels.yesterday;
	}

	if (daysAgo === 2) {
		return labels.dayBeforeYesterday;
	}

	const date = moment(day, DATE_FORMAT);
	return (locale ? date.clone().locale(locale) : date).format('LL');
};
